import { useQuery } from '@tanstack/react-query';
import {
  Activity, FileText, CheckCircle2, XCircle, Edit3, UserCog, Plus,
  Trash2, LogIn, Settings,
} from 'lucide-react';
import { useT } from '@/store/i18n';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { api } from '@/lib/api';

interface ActivityRow {
  action: string;
  details?: string;
  created_at: string;
  user_name?: string;
  description?: string;
  icon?: string;
  color?: string;
}

const ICON_BY_ACTION: Record<string, React.ReactNode> = {
  create: <Plus className="w-4 h-4" />,
  update: <Edit3 className="w-4 h-4" />,
  delete: <Trash2 className="w-4 h-4" />,
  approve: <CheckCircle2 className="w-4 h-4" />,
  reject: <XCircle className="w-4 h-4" />,
  login: <LogIn className="w-4 h-4" />,
  user: <UserCog className="w-4 h-4" />,
  question: <FileText className="w-4 h-4" />,
  settings: <Settings className="w-4 h-4" />,
};

const TONE_BY_COLOR: Record<string, string> = {
  green: 'bg-emerald-100 text-emerald-700',
  emerald: 'bg-emerald-100 text-emerald-700',
  blue: 'bg-blue-100 text-blue-700',
  red: 'bg-red-100 text-red-700',
  purple: 'bg-violet-100 text-violet-700',
  orange: 'bg-amber-100 text-amber-700',
  amber: 'bg-amber-100 text-amber-700',
  gray: 'bg-slate-100 text-slate-700',
};

function pickIcon(action: string, fallbackKey?: string): React.ReactNode {
  const a = action.toLowerCase();
  if (a.includes('create') || a.includes('add')) return ICON_BY_ACTION.create;
  if (a.includes('update') || a.includes('edit')) return ICON_BY_ACTION.update;
  if (a.includes('delete') || a.includes('remove')) return ICON_BY_ACTION.delete;
  if (a.includes('approve')) return ICON_BY_ACTION.approve;
  if (a.includes('reject')) return ICON_BY_ACTION.reject;
  if (a.includes('login')) return ICON_BY_ACTION.login;
  if (a.includes('user')) return ICON_BY_ACTION.user;
  if (a.includes('question')) return ICON_BY_ACTION.question;
  return fallbackKey ? ICON_BY_ACTION[fallbackKey] ?? <Activity className="w-4 h-4" /> : <Activity className="w-4 h-4" />;
}

function timeAgo(iso: string, t: (k: string) => string) {
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return t('time.just_now');
  if (diff < 3600) return `${Math.floor(diff / 60)} ${t('time.minutes_ago')}`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} ${t('time.hours_ago')}`;
  if (diff < 604800) return `${Math.floor(diff / 86400)} ${t('time.days_ago')}`;
  return new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
}

export function ActivityLog() {
  const t = useT();
  const list = useQuery({
    queryKey: ['activity-log'],
    queryFn: async () => {
      const r = await api<{ data?: ActivityRow[] }>('dashboard/recent-activity.php?limit=100');
      return r.data ?? [];
    },
    refetchInterval: 30_000,
  });

  return (
    <div className="space-y-5">
      <PageHeader
        title={t('activity.title')}
        description={t('activity.desc')}
      />

      <Card>
        {list.isLoading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-12 w-full" />
            ))}
          </div>
        ) : (list.data ?? []).length === 0 ? (
          <EmptyState
            icon={<Activity className="w-7 h-7" />}
            title={t('activity.empty')}
            description={t('activity.empty_desc')}
          />
        ) : (
          <ol className="divide-y divide-slate-100">
            {(list.data ?? []).map((a, i) => {
              const tone = TONE_BY_COLOR[a.color ?? 'gray'] ?? TONE_BY_COLOR.gray;
              return (
                <li key={i} className="px-5 py-3 flex items-start gap-3 hover:bg-slate-50/70">
                  <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
                    {pickIcon(a.action, a.icon)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-900 leading-snug">
                      {a.user_name && (
                        <span className="font-semibold">{a.user_name}</span>
                      )}{' '}
                      <span className="text-slate-700">{a.description ?? a.action}</span>
                    </p>
                    {a.details && a.details !== a.description && (
                      <p className="text-xs text-slate-500 mt-0.5 truncate">{a.details}</p>
                    )}
                    <div className="text-[11px] text-slate-400 mt-1">{timeAgo(a.created_at, t)}</div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
      </Card>
    </div>
  );
}
